// ---------------------------------------------------------------------------
// Rendering performance modes for headless / automated browsers.
// ---------------------------------------------------------------------------
// We expose TWO independent flags because the headless-Chromium crash
// has two distinct trigger surfaces:
//
//   1. The high-frequency CANVASES (SparkleRainCanvas + CursorTrail)
//      that draw at ~60 fps. These reliably OOM the software renderer
//      and must ALWAYS be disabled in any headless context — even when
//      we want to verify the real production visual stack.
//
//   2. The expensive STATIC CSS (backdrop-filter on bubbles + composer,
//      stacked radial gradients on body::before / bg-stage). These only
//      need to be disabled when we want the absolute lightest possible
//      rendering — e.g. for fast automated end-to-end smoke tests that
//      don't care what the page looks like, only that selectors work.
//
// Two booleans, two purposes:
//
//   isLiteMode()
//       → "the lightest possible rendering — also strip CSS"
//       Triggered ONLY by an explicit `?lite=1` / `?lite=true` URL
//       param. Adds `html.lite-mode` so the CSS overrides in index.css
//       activate. Implies shouldSkipCanvases() == true.
//
//   shouldSkipCanvases()
//       → "do not mount SparkleRainCanvas / CursorTrail / register SW"
//       Triggered by isLiteMode() OR by a HeadlessChrome / Playwright /
//       Puppeteer user agent. Lets me load the production visual stack
//       (with all the backgrounds + blurs) in Playwright for debugging
//       VISUAL issues without instantly crashing the renderer.
//
//   Both honour explicit `?lite=0` / `?canvases=on` as override-OFF
//   knobs for cases where I really want to attempt the full stack.
// ---------------------------------------------------------------------------

function _readParams() {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search || '');
  } catch (_e) {
    return null;
  }
}

function _hasHeadlessUA() {
  if (typeof window === 'undefined') return false;
  const ua = (window.navigator && window.navigator.userAgent) || '';
  return /HeadlessChrome|Headless|Puppeteer|Playwright/i.test(ua);
}

/**
 * Activates `html.lite-mode` + heaviest CSS overrides.
 * STRICT: only true for explicit `?lite=1` (no UA auto-detection).
 */
export function isLiteMode() {
  const params = _readParams();
  if (!params) return false;
  const v = params.get('lite');
  if (v === '0' || v === 'false' || v === 'no') return false;
  return v === '1' || v === 'true' || v === 'yes';
}

/**
 * Skip the expensive 60fps canvases + service worker registration.
 * True if isLiteMode() OR any headless / automation UA — UNLESS
 * the caller has explicitly opted IN to canvases via `?canvases=on`.
 */
export function shouldSkipCanvases() {
  const params = _readParams();
  if (params) {
    const c = params.get('canvases');
    if (c === 'on' || c === '1' || c === 'true') return false;
  }
  if (isLiteMode()) return true;
  return _hasHeadlessUA();
}
